import React, { useState, useRef } from 'react';
import { FiX, FiUploadCloud, FiCheck, FiAlertTriangle, FiFileText, FiEdit2, FiDatabase } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import Api from '../../Api';
import './ImportEmployeesModal.css'; // Reusing CSS

export default function ImportEmployeeRecordModal({
  isOpen,
  onClose,
  companyId,
  onSuccess,
}) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Por favor, selecione um arquivo PDF.');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        toast.error('Por favor, selecione um arquivo PDF.');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleImport = async () => {
    if (!companyId) {
      toast.error('Selecione uma empresa para importar.');
      return;
    }
    if (!file) return;

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await Api.post(
        `/companies/${companyId}/employees/import-record`,
        formData
      );

      setResult(response.data);
      toast.success(`Processado: ${response.data.processed} registros.`);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar arquivo.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importar Ficha de Registro (PDF)"
      width="800px"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
          {!result ? (
            <>
              <div className="step-description">
                Selecione o arquivo PDF contendo as fichas de registro (uma página por funcionário).
                O sistema irá extrair os dados e atualizar ou criar os funcionários com base no CPF.
              </div>

              <div
                className={`upload-area ${file ? 'has-file' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf"
                  hidden
                />
                {file ? (
                  <div className="file-info-container">
                    <FiFileText size={48} className="upload-icon" style={{ color: 'var(--primary-color)' }} />
                    <div className="file-info-details">
                      <strong className="file-info-name">{file.name}</strong>
                      <span className="file-info-size">{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <Button
                      variant="text"
                      color="danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      <FiX size={20} /> Remover Arquivo
                    </Button>
                  </div>
                ) : (
                  <>
                    <FiUploadCloud className="upload-icon" />
                    <p className="upload-text">Clique ou arraste o arquivo PDF aqui</p>
                    <span className="upload-subtext">Apenas arquivos .pdf</span>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="import-result">
              <div className="result-summary">
                <div className="result-item success">
                  <FiCheck size={24} />
                  <div className="result-item-content">
                    <strong>{result.processed}</strong>
                    <span>Processados</span>
                  </div>
                </div>
                <div className="result-item info">
                  <FiUploadCloud size={24} />
                  <div className="result-item-content">
                    <strong>{result.created}</strong>
                    <span>Criados</span>
                  </div>
                </div>
                <div className="result-item warning">
                  <FiEdit2 size={24} />
                  <div className="result-item-content">
                    <strong>{result.updated}</strong>
                    <span>Atualizados</span>
                  </div>
                </div>
                <div className="result-item error">
                  <FiAlertTriangle size={24} />
                  <div className="result-item-content">
                    <strong>{result.failed}</strong>
                    <span>Falhas</span>
                  </div>
                </div>
                {(result.processed - result.created - result.updated) > 0 && (
                  <div className="result-item neutral">
                    <FiDatabase size={24} />
                    <div className="result-item-content">
                      <strong>{result.processed - result.created - result.updated}</strong>
                      <span>Sincronizados</span>
                    </div>
                  </div>
                )}
              </div>

              {result.errors.length > 0 && (
                <div className="error-list">
                  <h4>Erros Encontrados:</h4>
                  <ul>
                    {result.errors.map((err, idx) => (
                      <li key={idx}>
                        Página {err.page}: {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-actions">
          {!result ? (
            <>
              <Button onClick={handleClose} className="cancel-button" disabled={loading}>
                Cancelar
              </Button>
              <Button
                onClick={handleImport}
                disabled={!file || loading}
                loading={loading}
              >
                Processar Importação
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>Fechar</Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
